import React, { useEffect, useState } from 'react';
import { SelectChangeEvent } from '@mui/material';
import axios from 'axios';
import { API_PATH } from '../../../App';
import { getAxiosConfig } from '../../../helpers/AuthHelper';
import { CustomerGroup, MachineModelInfo } from '../Machines/types';
import SortableTable from './WorkerTable';
import SearchFilters from './WorkerSearchFilterBar';
import PaginationControls from '../GlobalComponents/Pagination';

export interface WorkerData {
    workerId: string;
    expenseInDollar?: number;
    machineId?: string | null;
    machineConfig?: string | null;
    machineModelName?: string | null;
    currency?: string;
    spend?: number;
    expenseDate?: string;
    workerEngine?: string;
    uptimeInHours?: number;
    hostingRate?: string | null;
    power?: string | null;
    totalCharge?: string | null;
    customerId?: string | null;
    dummy?: boolean | null;
    machineType?: 'PHYSICAL' | 'VIRTUAL' | null;
    hashRate?: number | null;
    hashUnit?: string | null;
    virtualHashRate?: number | null;
    machineModelHashRate?: number | null;
    effectiveHashRate?: number | null;
}

interface WorkerProps {
    fromDate: Date | null;
    setFromDate: (date: Date | null) => void;
    toDate: Date | null;
    setToDate: (date: Date | null) => void;
    searchTrigger: boolean;
}

interface SearchOption {
    id: number;
    name: string;
}

const WorkerData: React.FC<WorkerProps> = ({ fromDate, setFromDate, toDate, setToDate, searchTrigger }) => {
    const [selectedPool, setSelectedPool] = useState<string>('ALL');
    const [searchMachineOptions, setSearchMachineOptions] = useState<SearchOption[]>([]);
    const [searchMachineTerm, setSearchMachineTerm] = useState<number[]>([]);
    const [searchGroupTerm, setSearchGroupTerm] = useState<string[]>([]);
    const [searchOptions, setSearchOptions] = useState<SearchOption[]>([]);
    const [searchTerm, setSearchTerm] = useState<number[]>([]);
    const [invoiceType, setInvoiceType] = useState<string>("")
    const [billingScheme, setBillingScheme] = useState<string>("")
    const [machineType, setMachineType] = useState<string>("")
    const [rows, setRows] = useState<any[]>([]);
    const [tableLoader, setTableLoader] = useState(false);
    const [searchKeyInput, setSearchKeyInput] = useState<string>('');
    const [rowsPerPage, setRowsPerPage] = useState<number>(200);
    const [pagination, setPagination] = useState({
        pageIndex: 0,
        totalPages: 0,
    });
    const [page, setPage] = useState(1)
    const [orderBy, setOrderBy] = useState<string>();
    const [order, setOrder] = useState<'ASC' | 'DESC'>();
    const [tableTotal, setTableTotal] = useState({
        spendInBtc: '0',
        spendInUsd: '0',
        hostingRate: '0',
        power: '0',
        spend: '0',
        totalCharge: '0',
        total_records: '0',
        uptimeInHours: '0',
        totalChargeInBtc: '0',
    })
    const [btcPrice, setBtcPrice] = useState(0)
    const [totalRecords, setTotalRecords] = useState(0)
    const [downloadData, setDownloadData] = useState<any[]>([])
    const [downloadLoader, setDownloadLoader] = useState<boolean>(false)
    const [activeDummy, setActiveDummy] = useState<boolean>(false)

    const [resellerList, setResellerList] = useState<SearchOption[]>([])
    const [selectedResellerIds, setSelectedResellerIds] = useState<number[]>([])

    useEffect(() => {
        fetchCustomerList();
        fetchMachineList();
        fetchResellerList();
    }, []);


    useEffect(() => {
        const fromTime = formatDate(fromDate);
        const toTime = formatDate(toDate);
        fetchWorkerData(fromTime, toTime, searchTerm, searchMachineTerm);
    }, [fromDate, toDate, searchTerm, searchMachineTerm, pagination.pageIndex, rowsPerPage, orderBy, order, searchGroupTerm, searchTrigger, activeDummy, selectedResellerIds, billingScheme, machineType]);

    useEffect(() => {
        if (downloadData.length > 0) {
            handleDownloadCSV();
        }
    }, [downloadData]);

    const fetchCustomerList = async () => {
        try {
            const response = await axios.get(`${API_PATH}/customer/list?orderBy=none`, getAxiosConfig());
            const dataList = response.data.map((item: CustomerGroup) => ({
                id: item.id,
                name: item.customerGroupName,
            }));
            dataList.unshift({ id: -1, name: 'unknown' });
            setSearchOptions(dataList);
        } catch (error) {
            console.error('Error fetching customer list:', error);
        }
    };

    const fetchMachineList = async () => {
        try {
            const response = await axios.get(`${API_PATH}/machine_model/list`, getAxiosConfig());
            const dataList = response.data.map((item: MachineModelInfo) => ({
                id: item.id,
                name: item.modelName,
            }));
            dataList.unshift({ id: -1, name: 'unknown' });
            setSearchMachineOptions(dataList);
        } catch (error) {
            console.error('Error fetching machine list:', error);
        }
    };

    const fetchResellerList = async () => {
        try {
            const response = await axios.get(`${API_PATH}/finance/report/resellers`, getAxiosConfig());
            const dataList = response.data.map((item: any) => ({
                id: item.id,
                name: item.resellerName,
            }));
            setResellerList(dataList);
        } catch (error) {
            console.error('Error fetching reseller list:', error);
        }
    };

    const formatDate = (date: Date | null): string => {
        if (!date) return '';
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const fetchWorkerData = (fromTime: string, toTime: string, customerIds: number[], modelIds: number[]) => {
        setTableLoader(true);
        axios.post(`${API_PATH}/finance/report/worker`,
            {
                customerIds,
                modelIds,
                fromTime,
                toTime,
                host: selectedPool,
                searchKey: searchKeyInput,
                page: page,
                size: rowsPerPage,
                sort: orderBy ? { [orderBy]: order } : {},
                groupBy: searchGroupTerm,
                dummy: activeDummy,
                invoiceType,
                billingScheme,
                machineType,
                resellerIds: selectedResellerIds
            }, getAxiosConfig())
            .then((response) => {
                const customerList = searchOptions;
                const updatedRows = response.data.data.map((row: any) => {
                    let customerName = "unknown";
                    let machineModelName = row.machineModelName;
                    if (row.customerId !== null && row.customerId !== 0 && row.customerId !== -1) {
                        const customer = customerList.find((customer) => customer.id === row.customerId);
                        customerName = customer ? customer.name : "unknown";
                    }
                    if (row.machineModelName === null) {
                        machineModelName = "unknown";
                    }

                    return {
                        ...row,
                        machineModelName,
                        customer: customerName,
                        effectiveHashRate: row.hashRate
                            ?? row.effectiveHashRate
                            ?? (row.machineType === 'VIRTUAL' ? row.virtualHashRate : row.machineModelHashRate)
                            ?? null,
                    };
                });

                setTotalRecords(Number(response.data?.total?.total_records))
                setRows(updatedRows);
                setTableTotal(response.data.total);

                setBtcPrice(response.data.btc)

                setPagination({
                    pageIndex: response.data.currentPage - 1,
                    totalPages: response.data.totalPages,
                });
                setTableLoader(false);
            })
            .catch((error) => {
                console.error('Error fetching worker data:', error);
            })
            .finally(() => {
                setTableLoader(false);
            });
    };

    const fetchAndDownloadAllWorkerData = (fromTime: string, toTime: string, customerIds: number[], modelIds: number[], totalRecords: number) => {
        setDownloadLoader(true)
        console.log("rowsPerPage", totalRecords);

        axios.post(`${API_PATH}/finance/report/worker`,
            {
                customerIds,
                modelIds,
                fromTime,
                toTime,
                host: selectedPool,
                searchKey: searchKeyInput,
                page: page,
                size: totalRecords,
                sort: orderBy ? { [orderBy]: order } : {},
                groupBy: searchGroupTerm,
                dummy: activeDummy,
                invoiceType,
                billingScheme,
                machineType,
                resellerIds: selectedResellerIds
            }, getAxiosConfig())
            .then((response) => {
                const customerList = searchOptions;
                const updatedRows = response.data.data.map((row: any) => {
                    let customerName = "unknown";
                    let machineModelName = row.machineModelName;
                    if (row.customerId !== null && row.customerId !== 0 && row.customerId !== -1) {
                        const customer = customerList.find((customer) => customer.id === row.customerId);
                        customerName = customer ? customer.name : "unknown";
                    }
                    if (row.machineModelName === null) {
                        machineModelName = "unknown";
                    }
                    return {
                        ...row,
                        machineModelName,
                        customer: customerName,
                        effectiveHashRate: row.hashRate
                            ?? row.effectiveHashRate
                            ?? (row.machineType === 'VIRTUAL' ? row.virtualHashRate : row.machineModelHashRate)
                            ?? null,
                    };
                });

                setDownloadData(updatedRows);
                setDownloadLoader(false)
            })
            .catch((error) => {
                console.error('Error fetching worker data:', error);
                setDownloadLoader(false)
            })
            .finally(() => {
                setTableLoader(false);
                setDownloadLoader(false)

            });
    };



    const handleSearch = () => {
        const fromTime = formatDate(fromDate);
        const toTime = formatDate(toDate);
        fetchWorkerData(fromTime, toTime, searchTerm, searchMachineTerm);
    };

    const handlePoolChange = (event: SelectChangeEvent<string>) => {
        setSelectedPool(event.target.value);
    };

    const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
        setPage(value)
        setPagination((prev) => ({
            ...prev,
            pageIndex: value - 1,
        }));
    };

    const handleRowsPerPageChange = (event: SelectChangeEvent<number>) => {
        const newRowsPerPage = Number(event.target.value);
        setPage(1)
        setRowsPerPage(newRowsPerPage);
        setPagination((prev) => ({
            ...prev,
            pageIndex: 0,
        }));
    };

    const handleRequestSort = (property: string) => {
        const isAsc = orderBy === property && order === 'ASC';
        setOrderBy(property);
        setOrder(isAsc ? 'DESC' : 'ASC');
    };

    const handleDownloadCSV = () => {
        const headers = [
            'Expense Date',
            'Worker ID',
            'Customer',
            'Reseller',
            'Machine Model',
            'Hashrate',
            'Hashrate Unit',
            'Coin',
            'Uptime (hrs)',
            'Power',
            'Hosting Rate',
            'Worker Engine',
            'Billing Scheme',
            'Expense(Coin)',
            'Expense(BTC)',
            'Total Charge(BTC)',
            'Expense($)',
            'Income($)',
            'P/L',
            'Dummy'
        ];
        const csvRows = [
            headers.join(','),
            ...downloadData.map(row => [
                row.expenseDate,
                row.workerId,
                row.customer,
                resellerList.find(r => r.id === row.resellerId)?.name || '-',
                row.machineModelName,
                row.effectiveHashRate ?? '',
                row.hashUnit || '',
                row.currency,
                row.uptimeInHours,
                row.power,
                row.hostingRate,
                row.workerEngine,
                row.hostingBillingScheme || '',
                row.spend?.toFixed(8),
                row.spendInBtc?.toFixed(8),
                row.totalChargeInBtc?.toFixed(8),
                row.spendInUsd?.toFixed(2),
                Number(row.totalCharge).toFixed(2),
                (Number(row.totalCharge) - row.spendInUsd)?.toFixed(4),
                row.dummy
            ].join(',')),
        ];

        const totalRow = [
            'Total',
            ...Array(13).fill(''),
            Number(tableTotal.spendInBtc).toFixed(8),
            Number(tableTotal.totalChargeInBtc).toFixed(8),
            (Number(tableTotal.spendInUsd)).toFixed(2),
            Number(tableTotal.totalCharge).toFixed(5),
            (Number(tableTotal.totalCharge) - (Number(tableTotal.spendInUsd))).toFixed(2),
        ].join(',');
        csvRows.push(totalRow);

        const csvString = csvRows.join('\n');
        const blob = new Blob([csvString], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);

        const a = document.createElement('a');
        a.href = url;
        a.download = `worker_report.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        window.URL.revokeObjectURL(url);
    };


    const CsvControl = () => {
        const fromTime = formatDate(fromDate);
        const toTime = formatDate(toDate);
        fetchAndDownloadAllWorkerData(fromTime, toTime, searchTerm, searchMachineTerm, totalRecords);
    }

    return (
        <>
            <SearchFilters
                selectedPool={selectedPool}
                handlePoolChange={handlePoolChange}
                searchOptions={searchOptions}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                searchMachineOptions={searchMachineOptions}
                searchMachineTerm={searchMachineTerm}
                setSearchMachineTerm={setSearchMachineTerm}
                searchGroupTerm={searchGroupTerm}
                setSearchGroupTerm={setSearchGroupTerm}
                searchKeyInput={searchKeyInput}
                setSearchKeyInput={setSearchKeyInput}
                handleSearch={handleSearch}
                handleDownloadCSV={CsvControl}
                totalRecords={totalRecords}
                downloadLoader={downloadLoader}
                invoiceType={invoiceType}
                setInvoiceType={setInvoiceType}
                billingScheme={billingScheme}
                setBillingScheme={setBillingScheme}
                machineType={machineType}
                setMachineType={setMachineType}
                setActiveDummy={setActiveDummy}
                activeDummy={activeDummy}
                setPage={setPage}
                resellerList={resellerList}
                selectedResellerIds={selectedResellerIds}
                setSelectedResellerIds={setSelectedResellerIds}
            />
            <SortableTable
                rows={rows}
                order={order || undefined}
                orderBy={orderBy || undefined}
                handleRequestSort={handleRequestSort}
                tableTotal={tableTotal}
                btc={btcPrice}
                isLoading={tableLoader}
                resellerList={resellerList}
            />
            <PaginationControls
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleRowsPerPageChange}
                totalPages={pagination.totalPages}
                currentPage={pagination.pageIndex + 1}
                onPageChange={handlePageChange}
            />

        </>
    );
};

export default WorkerData;
